<template>
  <div class="contents" v-loading="loading">
    <div class="title flexB">
      <h2>레벨 테스트 세션 상세</h2>
    </div>

    <!-- <Navigation /> -->

    <div class="box one" v-if="session">
      <div class="detailGrid">
        <div><span>사용자</span>{{ session.user?.nickname || "-" }}</div>
        <div><span>이메일</span>{{ session.user?.email || "-" }}</div>
        <div>
          <span>현재 레벨</span>{{ getLevelLabel(session.user?.level) }}
        </div>
        <div><span>도전 레벨</span>{{ getLevelLabel(session.examLevel) }}</div>
        <div><span>상태</span>{{ getLabel(statuses, session.status) }}</div>
        <div><span>결과</span>{{ getLabel(outcomes, session.outcome) }}</div>
        <div><span>제출 시각</span>{{ formatDate(session.submittedAt) }}</div>
        <div><span>검토 완료</span>{{ formatDate(session.finalizedAt) }}</div>
      </div>
    </div>

    <div class="box one" v-if="session">
      <h2 class="mb20">문항별 제출 내용</h2>
      <div
        class="sessionItem"
        v-for="(data, i) in reviewedItems"
        :key="i"
      >
        <div class="sessionItemHeader">
          <h3>{{ i + 1 }}. {{ getLabel(questionTypes, data.questionType) }}</h3>
          <p>자동 채점: {{ getAutoCorrectLabel(data.autoCorrect) }}</p>
        </div>
        <p class="flex question">
          <strong>지시문</strong> {{ data.instructionText || "-" }}
        </p>
        <p class="flex question">
          <strong>{{
            data.questionType === "speaking_listen" ? "질문(TTS)" : "문제 문구"
          }}</strong>
          {{ data.promptText || "-" }}
        </p>
        <p v-if="data.promptTextEn" class="flex question">
          <strong>번역(영어)</strong> {{ data.promptTextEn }}
        </p>
        <p v-if="data.promptTextJp" class="flex question">
          <strong>번역(일본어)</strong> {{ data.promptTextJp }}
        </p>
        <p v-if="data.questionType !== 'speaking_listen'" class="flex question">
          <strong>정답</strong>
          {{
            Array.isArray(data.correctAnswers) && data.correctAnswers.length > 0
              ? data.correctAnswers.join(", ")
              : "-"
          }}
        </p>
        <p class="flex question">
          <strong>사용자 답안</strong>
          <em class="userAnswer">{{ formatAnswer(data.userAnswer) }}</em>
        </p>
        <!-- <p v-if="data.sttText" class="flex question">
          <strong>STT 텍스트</strong> {{ data.sttText }}
        </p> -->
        <p v-if="data.audioUrl" class="flex question">
          <strong>사용자 음성</strong>
          <audio
            controls
            :src="data.audioUrl"
            preload="none"
            class="audioPlayer"
          >
            브라우저가 오디오 재생을 지원하지 않습니다.
          </audio>
        </p>

        <div class="reviewBox" v-if="data.questionType === 'speaking_listen'">
          <div class="flex">
            <p>수동 채점</p>
            <el-select
              v-model="data.manualDecision"
              placeholder="선택"
              :disabled="isFinalized"
            >
              <el-option label="정답 처리" :value="'accepted'" />
              <el-option label="오답 처리" :value="'rejected'" />
              <el-option label="보류" :value="'pending'" />
            </el-select>
          </div>
          <div class="flex double">
            <p>피드백</p>
            <textarea class="full" v-model="data.reviewNote" />
          </div>
        </div>
      </div>
    </div>

    <div class="box one" v-if="session">
      <h2 class="mb20">세션 확정</h2>
      <p v-if="isFinalized" class="passedLockNotice">
        확정된 세션은 수정할 수 없습니다.
      </p>
      <div class="inputBox">
        <div class="flex">
          <p>결과</p>
          <el-select
            v-model="finalOutcome"
            :disabled="isFinalized"
            placeholder="결과를 선택해 주세요"
          >
            <el-option
              v-for="item in outcomes.filter(
                (item) => item.value !== 'pending' && item.value !== 'failed',
              )"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </div>
        <div class="flex">
          <p>확정 후 레벨</p>
          <el-input :value="calculatedFinalLevelLabel" disabled></el-input>
        </div>
        <!-- <div class="flex double">
          <p>관리자 메모</p>
          <textarea
            class="full"
            v-model="reviewSummary"
            :disabled="isFinalized"
          />
        </div> -->
      </div>

      <div class="buttonWrap">
        <button class="basic" :disabled="isFinalized" @click="finalizeSession">
          세션 확정
        </button>
      </div>
    </div>

    <div class="buttonWrap">
      <router-link :to="backLink">
        <span class="material-icons-round"> keyboard_backspace </span>목록
      </router-link>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { fetchLevelTestSession, finalizeLevelTestSession } from "@/api/admin";
import {
  getLevelLabel,
  LEVEL_TEST_OUTCOMES,
  LEVEL_TEST_QUESTION_TYPES,
  LEVEL_TEST_STATUSES,
} from "@/config/levelTest";

export default {
  name: "levelTestSessionDetail",
  // components: {
  //   Navigation,
  // },
  data() {
    return {
      loading: false,
      moment,
      sessionId: "",
      session: null,
      reviewedItems: [],
      finalOutcome: "",
      reviewSummary: "",
      outcomes: LEVEL_TEST_OUTCOMES,
      statuses: LEVEL_TEST_STATUSES,
      questionTypes: LEVEL_TEST_QUESTION_TYPES,
    };
  },
  computed: {
    backLink() {
      const query = {};
      ["page", "searchWord", "status", "outcome", "level"].forEach((key) => {
        if (this.$route.query[key]) {
          query[key] = this.$route.query[key];
        }
      });
      return Object.keys(query).length
        ? { name: "levelTestSessionManage", query }
        : { name: "levelTestSessionManage" };
    },
    calculatedFinalLevel() {
      const currentLevel = Number(this.session?.user?.level || 0);
      if (this.finalOutcome === "passed") {
        return String(Math.min(currentLevel + 1, 4));
      }
      return String(currentLevel);
    },
    calculatedFinalLevelLabel() {
      return this.getLevelLabel(this.calculatedFinalLevel);
    },
    /** 확정 완료 후 재수정·재확정 방지 */
    isFinalized() {
      return this.session?.status === "finalized";
    },
    // isFinalized() {
    //   return (
    //     this.session?.status === "finalized" &&
    //     this.session?.outcome === "passed"
    //   );
    // },
  },
  created() {
    this.$store.dispatch("SET_NAVBAR", "6");
  },
  mounted() {
    this.sessionId = this.$route.query.id;
    this.getSessionDetail();
  },
  methods: {
    formatDate(value) {
      return value ? moment(value).format("YYYY.MM.DD HH:mm") : "-";
    },
    getLevelLabel,
    formatAnswer(answer) {
      if (Array.isArray(answer)) {
        return answer.join(", ");
      }
      if (typeof answer === "object" && answer !== null) {
        return JSON.stringify(answer);
      }
      return answer || "-";
    },
    getLabel(options, value) {
      const matched = options.find((item) => item.value === value);
      return matched ? matched.label : value || "-";
    },
    getAutoCorrectLabel(value) {
      if (value === true) return "정답";
      if (value === false) return "오답";
      return "수동 검토";
    },
    normalizeItems(items = []) {
      return items.map((item) => ({
        ...item,
        manualDecision: item.manualDecision || "pending",
        reviewNote: item.reviewNote || "",
      }));
    },
    getSessionDetail() {
      this.loading = true;
      fetchLevelTestSession(this.sessionId)
        .then((res) => {
          if (res.data.status === 200) {
            console.log("fetchLevelTestSession", res);
            this.session = res.data.data;
            this.reviewedItems = this.normalizeItems(
              res.data.data?.items || [],
            );
            this.finalOutcome =
              res.data.data?.outcome && res.data.data.outcome !== "pending"
                ? res.data.data.outcome
                : "";
            this.reviewSummary = res.data.data?.reviewSummary || "";
          } else {
            alert(res.data.message || "세션 상세를 불러오지 못했습니다.");
          }
        })
        .catch((err) => {
          alert(err.message);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    finalizeSession() {
      if (this.isFinalized) {
        return;
      }
      const hasPendingManualDecision = this.reviewedItems.some(
        (item) =>
          item.questionType === "speaking_listen" &&
          (!item.manualDecision || item.manualDecision === "pending"),
      );
      if (hasPendingManualDecision) {
        return alert(
          "수동 채점 문항은 '정답 처리' 또는 '오답 처리'를 모두 선택해 주세요.",
        );
      }
      if (!this.finalOutcome) {
        return alert("결과를 선택해 주세요.");
      }

      const reviewedItems = this.reviewedItems.map((item) => ({
        problemId: item.problemId,
        manualDecision: item.manualDecision,
        reviewNote: item.reviewNote,
      }));

      const payload = {
        outcome: this.finalOutcome,
        reviewSummary: this.reviewSummary,
        reviewedItems,
      };

      finalizeLevelTestSession(this.sessionId, payload)
        .then((res) => {
          if (res.data.status === 200) {
            alert("세션이 확정 되었습니다.");
            this.$router.push(this.backLink);
          } else {
            alert(res.data.message || "세션 확정에 실패했습니다.");
          }
        })
        .catch((err) => {
          const msg =
            err.response?.data?.message ||
            err.message ||
            "세션 확정에 실패했습니다.";
          alert(msg);
        });
    },
  },
};
</script>

<style scoped lang="scss">
.passedLockNotice {
  margin: 0 0 16px;
  color: #c45656;
  font-size: 14px;
}
.levelTestNav {
  margin: 20px 0 30px 0;
}
.detailGrid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 20px;
}

.detailGrid div {
  display: flex;
  gap: 12px;
}

.detailGrid span {
  width: 120px;
  font-weight: 700;
}
.sessionItem {
  p {
    margin-bottom: 8px;
    strong {
      width: 120px;
    }
  }
}
.userAnswer {
  color: #ff5b9f;
  font-weight: 600;
}
.audioPlayer {
  max-width: 100%;
}
.sessionItem + .sessionItem {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
}

.sessionItemHeader {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.reviewBox {
  margin-top: 12px;
  padding: 16px;
  background: #f8fafc;
  .flex {
    margin-bottom: 10px;
  }
  p {
    width: 120px;
    line-height: 40px;
  }
}
.inputBox {
  > .flex {
    margin-bottom: 10px;
  }

  p {
    width: 120px;
    line-height: 40px;
  }
}
textarea {
  min-height: 90px;
}
</style>
